import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Send, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { notasFiscaisApi, formatarMoeda, mensagemErro, NotaFiscalApiError } from '@/lib/notasFiscaisApi';
import {
  FORMAS_PAGAMENTO,
  MODALIDADES_FRETE,
  PRESENCA_COMPRADOR,
  type AmbienteFiscal,
  type ContaReceberOpcao,
  type DestinatarioNota,
  type ItemNota,
  type NotaFiscal,
  type PagamentoNota,
  type ProdutoRevenda,
  type RegraFiscal,
} from '@/types/notasFiscais';
import { DestinatarioForm } from './DestinatarioForm';
import { ItensNotaEditor, totalItem } from './ItensNotaEditor';

const DESTINATARIO_VAZIO: DestinatarioNota = {
  tipo_pessoa: 'PF',
  nome: '',
  cpf: '',
  cnpj: '',
  indicador_ie: 9,
  inscricao_estadual: '',
  email: '',
  telefone: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  municipio: '',
  codigo_municipio: '',
  uf: 'SC',
};

function itemParaCopia(item: ItemNota): ItemNota {
  return {
    produto_id: item.produto_id,
    codigo: item.codigo,
    descricao: item.descricao,
    ncm: item.ncm,
    cest: item.cest,
    origem: item.origem,
    unidade: item.unidade,
    quantidade: item.quantidade,
    valor_unitario: item.valor_unitario,
    valor_desconto: item.valor_desconto,
    regra_fiscal_id: item.regra_fiscal_id,
  };
}

interface NotaFiscalFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notaId: string | null;
  baseId?: string | null;
  ambiente: AmbienteFiscal;
  produtos: ProdutoRevenda[];
  regras: RegraFiscal[];
  onSalva: (nota: NotaFiscal, opcoes: { emitida: boolean; manterAberto: boolean }) => void;
}

export function NotaFiscalFormDialog({
  open,
  onOpenChange,
  notaId,
  baseId = null,
  ambiente,
  produtos,
  regras,
  onSalva,
}: NotaFiscalFormDialogProps) {
  const { toast } = useToast();
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmarEmissao, setConfirmarEmissao] = useState(false);
  const [errosEmissao, setErrosEmissao] = useState<string[]>([]);

  const [destinatario, setDestinatario] = useState<DestinatarioNota>(DESTINATARIO_VAZIO);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [salvarNoCadastro, setSalvarNoCadastro] = useState(true);
  const [itens, setItens] = useState<ItemNota[]>([]);
  const [pagamento, setPagamento] = useState<PagamentoNota>({ forma: '17', indicador: 0 });
  const [modalidadeFrete, setModalidadeFrete] = useState(9);
  const [valorFrete, setValorFrete] = useState(0);
  const [presenca, setPresenca] = useState(9);
  const [consumidorFinal, setConsumidorFinal] = useState(true);
  const [informacoes, setInformacoes] = useState('');
  const [contaReceber, setContaReceber] = useState<ContaReceberOpcao | null>(null);
  const [contaReceberId, setContaReceberId] = useState<string | null>(null);
  const [buscaConta, setBuscaConta] = useState('');
  const [contas, setContas] = useState<ContaReceberOpcao[]>([]);

  useEffect(() => {
    if (open) setErrosEmissao([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setContaReceber(null);
    const origemId = notaId || baseId;
    if (!origemId) {
      setDestinatario(DESTINATARIO_VAZIO);
      setLeadId(null);
      setItens([]);
      setPagamento({ forma: '17', indicador: 0 });
      setModalidadeFrete(9);
      setValorFrete(0);
      setPresenca(9);
      setConsumidorFinal(true);
      setInformacoes('');
      setContaReceberId(null);
      return;
    }
    const copiando = !notaId;
    setCarregando(true);
    notasFiscaisApi
      .obterNota(origemId)
      .then((nota) => {
        setDestinatario({ ...DESTINATARIO_VAZIO, ...nota.destinatario });
        setLeadId(nota.lead_id);
        setItens((nota.itens || []).map((item) => (copiando ? itemParaCopia(item) : item)));
        setPagamento({ forma: '17', indicador: 0, ...nota.pagamento } as PagamentoNota);
        setModalidadeFrete(nota.modalidade_frete);
        setValorFrete(Number(nota.valor_frete) || 0);
        setPresenca(nota.presenca_comprador);
        setConsumidorFinal(nota.consumidor_final === 1);
        setInformacoes(nota.informacoes_complementares || '');
        setContaReceberId(copiando ? null : nota.conta_receber_id);
      })
      .catch((e) => toast({ title: 'Erro ao carregar nota', description: mensagemErro(e), variant: 'destructive' }))
      .finally(() => setCarregando(false));
  }, [open, notaId, baseId, toast]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      notasFiscaisApi.buscarContasReceber(buscaConta.trim()).then(setContas).catch(() => setContas([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [buscaConta, open]);

  useEffect(() => {
    if (destinatario.tipo_pessoa === 'PF' || destinatario.indicador_ie === 9) setConsumidorFinal(true);
  }, [destinatario.tipo_pessoa, destinatario.indicador_ie]);

  const totais = useMemo(() => {
    const produtosTotal = itens.reduce(
      (acc, i) => acc + Math.round((i.quantidade || 0) * (i.valor_unitario || 0) * 100) / 100,
      0
    );
    const liquido = itens.reduce((acc, i) => acc + totalItem(i), 0);
    return { produtos: produtosTotal, desconto: produtosTotal - liquido, total: liquido + (valorFrete || 0) };
  }, [itens, valorFrete]);

  const montarCorpo = (): Partial<NotaFiscal> => ({
    lead_id: leadId,
    conta_receber_id: contaReceberId,
    destinatario,
    itens,
    pagamento,
    modalidade_frete: modalidadeFrete,
    valor_frete: modalidadeFrete === 9 ? 0 : valorFrete,
    presenca_comprador: presenca,
    consumidor_final: consumidorFinal ? 1 : 0,
    informacoes_complementares: informacoes,
  });

  const salvarCadastroCliente = async () => {
    if (!leadId || !salvarNoCadastro) return;
    try {
      await notasFiscaisApi.salvarDadosFiscaisCliente(leadId, {
        email: destinatario.email,
        cep: destinatario.cep,
        endereco: destinatario.logradouro,
        numero: destinatario.numero,
        complemento: destinatario.complemento,
        bairro: destinatario.bairro,
        cidade: destinatario.municipio,
        estado: destinatario.uf,
        codigo_municipio: destinatario.codigo_municipio,
        inscricao_estadual: destinatario.inscricao_estadual,
        indicador_ie: destinatario.tipo_pessoa === 'PF' ? 9 : destinatario.indicador_ie,
      });
    } catch (e) {
      toast({ title: 'Cliente não atualizado', description: mensagemErro(e), variant: 'destructive' });
    }
  };

  const salvar = async (emitir: boolean) => {
    setSalvando(true);
    setErrosEmissao([]);
    let nota: NotaFiscal | null = null;
    try {
      const corpo = montarCorpo();
      nota = notaId ? await notasFiscaisApi.salvarNota(notaId, corpo) : await notasFiscaisApi.criarNota(corpo);
      await salvarCadastroCliente();
      if (!emitir) {
        toast({ title: 'Rascunho salvo' });
        onSalva(nota, { emitida: false, manterAberto: false });
        return;
      }
      const emitida = await notasFiscaisApi.emitirNota(nota.id);
      toast({ title: 'Nota enviada para a SEFAZ', description: 'Acompanhe o status na tela da nota.' });
      onSalva(emitida, { emitida: true, manterAberto: false });
    } catch (e) {
      const detalhes = e instanceof NotaFiscalApiError ? e.detalhes : [];
      setErrosEmissao(detalhes.length ? detalhes : [mensagemErro(e)]);
      toast({ title: emitir ? 'Nota não emitida' : 'Erro ao salvar', description: mensagemErro(e), variant: 'destructive' });
      if (nota) onSalva(nota, { emitida: false, manterAberto: true });
    } finally {
      setSalvando(false);
      setConfirmarEmissao(false);
    }
  };

  const contaSelecionadaLabel = (() => {
    const c = contaReceber || contas.find((x) => x.id === contaReceberId);
    if (!c) return contaReceberId ? 'Conta vinculada' : 'Nenhuma';
    return `${c.descricao}${c.cliente ? ` - ${c.cliente}` : ''} (${formatarMoeda(c.valor)})`;
  })();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {notaId ? 'Editar NF-e' : baseId ? 'Nova NF-e (copiada de outra nota)' : 'Nova NF-e de venda'}
            </DialogTitle>
            <DialogDescription>
              Revenda de mercadoria - ambiente{' '}
              <strong>{ambiente === 'producao' ? 'PRODUÇÃO (com valor fiscal)' : 'homologação (teste, sem valor fiscal)'}</strong>
            </DialogDescription>
          </DialogHeader>

          {carregando ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              <section className="space-y-3">
                <h3 className="font-semibold">1. Destinatário</h3>
                <DestinatarioForm
                  destinatario={destinatario}
                  onChange={setDestinatario}
                  leadId={leadId}
                  onSelecionarCliente={setLeadId}
                />
                {leadId && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={salvarNoCadastro} onCheckedChange={(v) => setSalvarNoCadastro(v === true)} />
                    Atualizar endereço e dados fiscais no cadastro do cliente
                  </label>
                )}
              </section>

              <Separator />

              <section className="space-y-3">
                <h3 className="font-semibold">2. Itens</h3>
                <ItensNotaEditor
                  itens={itens}
                  onChange={setItens}
                  produtos={produtos}
                  regras={regras}
                  destinatario={destinatario}
                />
              </section>

              <Separator />

              <section className="space-y-3">
                <h3 className="font-semibold">3. Pagamento, frete e operação</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label>Forma de pagamento</Label>
                    <Select value={pagamento.forma} onValueChange={(v) => setPagamento({ ...pagamento, forma: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FORMAS_PAGAMENTO.map((f) => <SelectItem key={f.valor} value={f.valor}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Condição</Label>
                    <Select
                      value={String(pagamento.indicador)}
                      onValueChange={(v) => setPagamento({ ...pagamento, indicador: v === '1' ? 1 : 0 })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">À vista</SelectItem>
                        <SelectItem value="1">A prazo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Frete</Label>
                    <Select value={String(modalidadeFrete)} onValueChange={(v) => setModalidadeFrete(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MODALIDADES_FRETE.map((m) => <SelectItem key={m.valor} value={String(m.valor)}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Valor do frete cobrado</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={modalidadeFrete === 9}
                      value={modalidadeFrete === 9 ? 0 : valorFrete}
                      onChange={(e) => setValorFrete(Number(e.target.value))}
                    />
                  </div>
                  {pagamento.forma === '99' && (
                    <div className="space-y-1 md:col-span-2">
                      <Label>Descrição do pagamento</Label>
                      <Input
                        value={pagamento.descricao || ''}
                        onChange={(e) => setPagamento({ ...pagamento, descricao: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label>Presença do comprador</Label>
                    <Select value={String(presenca)} onValueChange={(v) => setPresenca(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRESENCA_COMPRADOR.map((p) => <SelectItem key={p.valor} value={String(p.valor)}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Consumidor final</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={consumidorFinal}
                        disabled={destinatario.tipo_pessoa === 'PF' || destinatario.indicador_ie === 9}
                        onCheckedChange={setConsumidorFinal}
                      />
                      <span className="text-sm text-muted-foreground">
                        {consumidorFinal ? 'Uso próprio' : 'Revenda/industrialização'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Vincular a uma conta a receber (a DANFE é anexada automaticamente)</Label>
                  <div className="flex flex-col md:flex-row gap-2">
                    <div className="relative md:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        placeholder="Buscar conta"
                        value={buscaConta}
                        onChange={(e) => setBuscaConta(e.target.value)}
                      />
                    </div>
                    <Select
                      value={contaReceberId || 'nenhuma'}
                      onValueChange={(v) => {
                        const id = v === 'nenhuma' ? null : v;
                        setContaReceberId(id);
                        setContaReceber(contas.find((c) => c.id === id) || null);
                      }}
                    >
                      <SelectTrigger className="md:flex-1"><SelectValue>{contaSelecionadaLabel}</SelectValue></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nenhuma">Nenhuma</SelectItem>
                        {contas.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.descricao}{c.cliente ? ` - ${c.cliente}` : ''} ({formatarMoeda(c.valor)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Informações complementares (opcional)</Label>
                  <Textarea
                    rows={2}
                    value={informacoes}
                    onChange={(e) => setInformacoes(e.target.value)}
                    placeholder="Ex.: pedido nº, referência do contrato. O texto do Simples Nacional é incluído automaticamente."
                  />
                </div>
              </section>

              <div className="rounded-md bg-muted/50 p-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div>Produtos: <strong>{formatarMoeda(totais.produtos)}</strong></div>
                <div>Descontos: <strong>{formatarMoeda(totais.desconto)}</strong></div>
                <div>Frete: <strong>{formatarMoeda(modalidadeFrete === 9 ? 0 : valorFrete)}</strong></div>
                <div className="text-base">Total: <strong>{formatarMoeda(totais.total)}</strong></div>
              </div>

              {errosEmissao.length > 0 && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm space-y-1">
                  <p className="font-medium text-destructive">Corrija antes de emitir:</p>
                  <ul className="list-disc pl-5">
                    {errosEmissao.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Fechar</Button>
            <Button variant="secondary" onClick={() => salvar(false)} disabled={salvando || carregando}>
              {salvando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salvar rascunho
            </Button>
            <Button onClick={() => setConfirmarEmissao(true)} disabled={salvando || carregando || itens.length === 0}>
              <Send className="h-4 w-4 mr-2" /> Emitir NF-e
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmarEmissao} onOpenChange={setConfirmarEmissao}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Emitir NF-e de {formatarMoeda(totais.total)}?</AlertDialogTitle>
            <AlertDialogDescription>
              {ambiente === 'producao'
                ? 'A nota será enviada à SEFAZ em PRODUÇÃO e terá valor fiscal. Depois de autorizada, só pode ser cancelada em até 24 horas.'
                : 'A nota será enviada ao ambiente de homologação (teste, sem valor fiscal).'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={salvando}
              onClick={(e) => {
                e.preventDefault();
                salvar(true);
              }}
            >
              {salvando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Emitir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
